import {StrictMode, Suspense, useEffect} from 'react'
import {createRoot} from 'react-dom/client'
import App from './App.tsx'
import './index.css'
import {Provider as ChakraProvider} from "./components/ui/provider";
import {Toaster} from "./components/ui/toaster";
import {Provider} from "react-redux";
import {store} from "./store/store.ts";
import {BrowserRouter, useLocation, useNavigationType, createRoutesFromChildren, matchRoutes} from "react-router-dom";
import './i18n';
import * as Sentry from "@sentry/react";
import {SentryErrorBoundary} from "./components/SentryErrorBoundary.tsx";
import {analytics} from "./utils/analytics.ts";
import {initDash0} from "./utils/observability.ts";

if (!import.meta.env.DEV) {
    const buildSha = (import.meta.env.VITE_BUILD_SHA as string | undefined) ?? 'dev';
    Sentry.init({
        dsn: "https://afb8a961b50ebb2d535ca93db3b4e5ff@o233657.ingest.us.sentry.io/4508129436827648",
        environment: import.meta.env.MODE,
        release: buildSha,
        sendDefaultPii: true,
        integrations: [
            Sentry.reactRouterV6BrowserTracingIntegration({
                useEffect,
                useLocation,
                useNavigationType,
                createRoutesFromChildren,
                matchRoutes,
                enableInp: true,
            }),
            Sentry.browserProfilingIntegration(),
            Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
        ],
        tracesSampleRate: 0.1,
        profilesSampleRate: 0.1,
        tracePropagationTargets: ["localhost", /^https:\/\/api\.barbermate\.eu/],
        // Record only when an error happens — keeps quota usage predictable.
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 1.0,
        ignoreErrors: [
            // Benign browser-internal layout signal Chrome fires when a ResizeObserver
            // callback re-triggers itself; not actionable.
            "ResizeObserver loop limit exceeded",
            "ResizeObserver loop completed with undelivered notifications",
            // User-cancelled fetch (route change, drawer close) — RTK Query aborts.
            "AbortError",
            // Stale chunk URL after a deploy. Reload handles this UX.
            "ChunkLoadError",
            "Loading chunk",
            "Failed to fetch dynamically imported module",
            // Injected by browser extension / Instagram in-app browser; not our code.
            "TrackerStorageType is not defined",
            "Java object is gone",
            // Instagram's iOS WebView injects sendDataToNative/sendPageHideMessage
            // scripts that touch window.webkit.messageHandlers and throw when the
            // handlers are absent (BARBERMATE-FE-3W). Not our code, not actionable.
            "window.webkit.messageHandlers",
        ],
        beforeSend(event) {
            if (event.extra) {
                delete event.extra.token;
                delete event.extra.refresh_token;
                delete event.extra.customerToken;
                delete event.extra.customerRefreshToken;
            }
            return event;
        },
        beforeBreadcrumb(breadcrumb) {
            if (breadcrumb.category === 'fetch' && breadcrumb.data?.url?.includes('token')) {
                return {
                    ...breadcrumb,
                    data: { ...breadcrumb.data, body: '[REDACTED]' },
                };
            }
            return breadcrumb;
        },
    });
    Sentry.setTag('app.build_sha', buildSha);
}

// Dash0 RUM — env-gated (no VITE_DASH0_* vars → no-op, zero network calls).
initDash0()

// Bootstrap analytics (sets gtag consent defaults, captures UTMs,
// initializes GA in consent-aware mode, loads Meta Pixel if user previously consented).
analytics.bootstrap()

createRoot(document.getElementById('root')!).render(
    <StrictMode>
        <Provider store={store}>
            <Suspense fallback={(<div>Loading</div>)}>
                <BrowserRouter>
                    <ChakraProvider defaultTheme="light" enableSystem>
                        <SentryErrorBoundary>
                            <App/>
                        </SentryErrorBoundary>
                        <Toaster/>
                    </ChakraProvider>
                </BrowserRouter>
            </Suspense>
        </Provider>
    </StrictMode>,
)
